import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Search, X } from 'lucide-react';
import { fetchGmp, selectGmpBoardRows } from '../../store/slices/gmpSlice';
import { fetchDrhpRegistry } from '../../store/slices/drhpSlice';
import { fetchCatalogIndex } from '../../store/slices/ipoCatalogSlice';
import { buildSearchIndex, rankSearchResults } from '../../utils/ipoSearch';

const FEED_MAX_AGE_MS = 10 * 60 * 1000;

const BOARD_PILL = {
  sme: 'border-amber-200 bg-amber-50 text-amber-700',
  mainboard: 'border-indigo-200 bg-indigo-50 text-indigo-700',
};

/**
 * Company search shared by the header and the hero.
 *
 * The feeds it searches are only requested when someone actually uses the box,
 * so a page nobody searches on pays nothing for it. They are the same feeds
 * other pages load, so they usually come back from the cache.
 */
export const IpoSearchBox = ({ inputClassName, placeholder = 'Search IPOs...', children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const gmpRows = useSelector(selectGmpBoardRows);
  const trackerRecords = useSelector((state) => state.drhp.records);
  const catalogRecords = useSelector((state) => state.ipoCatalog.index.records);

  const index = useMemo(
    () => buildSearchIndex({ gmpRows, trackerRecords, catalogRecords }),
    [gmpRows, trackerRecords, catalogRecords]
  );
  const results = useMemo(() => rankSearchResults(index, query), [index, query]);

  // Loaded on first use rather than on mount: the header is on every page
  const loadFeeds = () => {
    if (loaded) return;
    setLoaded(true);
    dispatch(fetchGmp({ maxAgeMs: FEED_MAX_AGE_MS }));
    dispatch(fetchDrhpRegistry({ maxAgeMs: FEED_MAX_AGE_MS }));
    dispatch(fetchCatalogIndex({ maxAgeMs: FEED_MAX_AGE_MS }));
  };

  useEffect(() => setActiveIndex(0), [query]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isOpen]);

  const go = (entry) => {
    if (!entry) return;
    setIsOpen(false);
    setQuery('');
    inputRef.current?.blur();
    navigate(entry.to);
  };

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      return;
    }
    if (!results.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => (current - 1 + results.length) % results.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      go(results[activeIndex]);
    }
  };

  const showPanel = isOpen && query.trim().length >= 2;
  const listId = 'ipo-search-results';

  return (
    <div ref={rootRef} className="relative w-full">
      <form
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          go(results[activeIndex]);
        }}
      >
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none z-10" aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={showPanel}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-label="Search IPOs by company name"
          value={query}
          placeholder={placeholder}
          onFocus={() => {
            loadFeeds();
            setIsOpen(true);
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
          }}
          onKeyDown={onKeyDown}
          className={inputClassName}
        />
        {query && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => {
              setQuery('');
              inputRef.current?.focus();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-1 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        {children}
      </form>

      {showPanel && (
        <div className="absolute left-0 right-0 top-full mt-2 z-50 rounded-2xl border border-slate-200 bg-white shadow-elevated overflow-hidden">
          {results.length === 0 ? (
            <p className="px-4 py-5 text-xs text-slate-500">
              {index.length ? `No IPO matches "${query.trim()}".` : 'Loading IPOs…'}
            </p>
          ) : (
            <ul id={listId} role="listbox" className="max-h-80 overflow-y-auto scrollbar-slim py-1">
              {results.map((entry, position) => (
                <li key={entry.key} role="option" aria-selected={position === activeIndex}>
                  <button
                    type="button"
                    // Keeps focus on the input, whose blur would close the panel first
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setActiveIndex(position)}
                    onClick={() => go(entry)}
                    className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left transition-colors ${
                      position === activeIndex ? 'bg-teal-50/70' : 'hover:bg-slate-50'
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-bold text-slate-900 truncate">{entry.company}</span>
                      <span className="block text-[11px] text-slate-400">{entry.detail}</span>
                    </span>
                    {entry.board && (
                      <span className={`px-2 py-0.5 rounded-md border text-[10px] font-bold whitespace-nowrap ${BOARD_PILL[entry.board]}`}>
                        {entry.board === 'sme' ? 'SME' : 'Mainboard'}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default IpoSearchBox;
